<template>
  <div>
    <div class="regra-modal__secao">
      <div class="regra-modal__secao-titulo">Tipo de regra *</div>
      <div class="regra-negocio-regra__tipo">
        <SeletorBusca
          :value="item.tipo"
          :opcoes="opcoesTipo"
          :mostrar-codigo="false"
          :clearable="false"
          placeholder="Selecione o tipo de regra"
          aria-label="Tipo de regra"
          @change="selecionarTipo"
        >
          <template #prefixo="{ opcao }">
            <fa-icon :icon="opcao.icone || 'sliders-h'" />
          </template>
          <template #opcao="{ opcao }">
            <span class="regra-modal__opcao">
              <span class="regra-modal__opcao-icone"><fa-icon :icon="opcao.icone" /></span>
              <span class="regra-modal__opcao-texto">
                <strong>{{ opcao.descricao }}</strong>
                <small>{{ opcao.resumo }}</small>
              </span>
            </span>
          </template>
        </SeletorBusca>
        <small v-if="tipoAtual" class="regra-modal__dica">{{ tipoAtual.descricao }}</small>
      </div>
      <small v-if="erros.tipo" class="regra-modal__campo-erro">{{ erros.tipo }}</small>
    </div>

    <div v-if="item.tipo === 'P'" class="regra-modal__secao">
      <div class="regra-modal__secao-titulo">Promoção</div>
      <div class="regra-modal__aviso">
        <fa-icon icon="info-circle" class="regra-modal__aviso-icone" />
        <span>
          O envio do pedido será vetado caso o pedido tenha mais itens em promoção do que o cadastrado na regra abaixo.
          Caso existam 2 ou mais regras cadastradas, certifique-se de que o período da proibição não entre em conflito,
          pois as regras são validadas de forma individual.
        </span>
      </div>
      <div class="regra-modal__frase mb-3">
        <span>O pedido pode ter no máximo</span>
        <input-percent class="form-control" v-model="item.perc_max_promocao" />
        <span>do valor em produtos de promoção.</span>
      </div>
      <ListaPromocoes
        :regra="item"
        :promocoes="promocoes"
        :promocoesSelecionadas="item.promocoesExcessao"
        :select-callback="addPromocao"
        :deselect-callback="removePromocao"
        :erro-carga="!!errosCarga.promocoes"
        @recarregar="$emit('recarregar', 'promocoes')"
      />
    </div>

    <div v-if="item.tipo === 'M'" class="regra-modal__secao">
      <div class="regra-modal__secao-titulo">Mix de produto</div>
      <div class="regra-modal__aviso">
        <fa-icon icon="info-circle" class="regra-modal__aviso-icone" />
        <span>
          O envio do pedido será vetado caso algum dos itens abaixo esteja fora das quantidades definidas.
          Ao vincular vários itens, a regra é validada por completo, ou seja, caso um dos itens seja incluído no pedido,
          todos os demais deverão estar no pedido nas quantidades especificadas.
        </span>
      </div>
      <ListaProdutos
        :regra="item"
        :produtos="produtos"
        :produtosSelecionados="item.produtos"
        :select-callback="addProduto"
        :deselect-callback="removeProduto"
        :hideQtd="false"
        :erro-carga="!!errosCarga.produtos"
        @recarregar="$emit('recarregar', 'produtos')"
      />
      <small v-if="erros.produtos" class="regra-modal__campo-erro">{{ erros.produtos }}</small>
    </div>

    <div v-if="item.tipo === 'MP'" class="regra-modal__secao">
      <div class="regra-modal__secao-titulo">Mix com percentual máximo</div>
      <div class="regra-modal__aviso">
        <fa-icon icon="info-circle" class="regra-modal__aviso-icone" />
        <span>
          O envio do pedido será vetado caso a soma dos itens selecionados e adicionados ao carrinho seja maior que o
          percentual máximo estipulado.
        </span>
      </div>
      <div class="regra-modal__frase mb-3">
        <span>O pedido pode ter no máximo</span>
        <input-percent class="form-control" v-model="item.perc_max_mix" />
        <span>do valor em produtos do mix.</span>
      </div>
      <ListaProdutos
        :regra="item"
        :produtos="produtos"
        :produtosSelecionados="item.produtos"
        :select-callback="addProduto"
        :deselect-callback="removeProduto"
        :hide-qtd="true"
        :erro-carga="!!errosCarga.produtos"
        @recarregar="$emit('recarregar', 'produtos')"
      />
    </div>

    <div v-if="item.tipo === 'D'" class="regra-modal__secao">
      <div class="regra-modal__secao-titulo">Dimensão</div>
      <div class="regra-modal__aviso">
        <fa-icon icon="info-circle" class="regra-modal__aviso-icone" />
        <span>
          O envio do pedido será vetado caso o segmento de venda não tenha um percentual mínimo de outro segmento em
          relação a ele. Exemplo: vetar o envio de pedido que contenha tubos caso nesse mesmo pedido não tenha 50% do
          valor em conexões.
        </span>
      </div>

      <InterruptorCashback
        rotulo="Todas as regras precisam ser válidas?"
        texto-ligado="Sim, todas as regras abaixo precisam ser válidas"
        texto-desligado="Não, basta uma das regras abaixo ser válida"
        :value="!!Number(item.validar_todas_regras)"
        @input="$set(item, 'validar_todas_regras', $event ? 1 : 0)"
      />

      <div class="regra-negocio-regra__dimensoes">
        <template v-for="(dimensaoRegra, indexDimensaoRegra) in item.dimensoes">
          <div v-if="indexDimensaoRegra" :key="`conector-${indexDimensaoRegra}`" class="regra-negocio-regra__conector">
            <span
              :class="[
                'regra-negocio-regra__conector-texto',
                { 'regra-negocio-regra__conector-texto--ou': !Number(item.validar_todas_regras) },
              ]"
            >
              {{ Number(item.validar_todas_regras) ? "E" : "OU" }}
            </span>
          </div>
          <div :key="`dimensao-${indexDimensaoRegra}`" class="regra-modal__caixa">
            <div class="regra-modal__caixa-cabecalho">
              <strong>Regra de validação {{ indexDimensaoRegra + 1 }}</strong>
              <CButton
                size="sm"
                color="danger"
                variant="outline"
                title="Excluir regra de validação"
                :aria-label="`Excluir a regra de validação ${indexDimensaoRegra + 1}`"
                @click="excluirRegra(indexDimensaoRegra)"
              >
                <fa-icon icon="trash-alt" />
              </CButton>
            </div>
            <div class="regra-negocio-regra__dimensao">
              <div class="regra-negocio-regra__dimensao-frase">Ao vender produtos do segmento:</div>
              <CRow>
                <CCol md="6" class="mb-2">
                  <label class="regra-modal__rotulo">Tipo de segmento</label>
                  <SeletorBusca
                    v-model="dimensaoRegra.coluna"
                    :opcoes="opcoesColunas"
                    :mostrar-codigo="false"
                    :clearable="false"
                    placeholder="Selecione o tipo de segmento"
                    aria-label="Tipo de segmento"
                    :erro="!!errosCarga.dimensoes"
                    @abrir="$emit('recarregar', 'dimensoes')"
                    @change="changeColunaSelecionada"
                  />
                </CCol>
                <CCol md="6" class="mb-2">
                  <label class="regra-modal__rotulo">Segmento</label>
                  <SeletorBusca
                    v-model="dimensaoRegra.dimensao_id"
                    :opcoes="opcoesSegmentos(dimensaoRegra.coluna)"
                    :clearable="false"
                    placeholder="Busque o segmento"
                    aria-label="Segmento"
                    :erro="!!errosCarga.dimensoes"
                    @abrir="$emit('recarregar', 'dimensoes')"
                  />
                </CCol>
              </CRow>
              <div class="regra-modal__frase my-2">
                <span>O segmento abaixo deverá representar no mínimo</span>
                <input-percent class="form-control" v-model="dimensaoRegra.perc_min_vinculado" />
                <span>e no máximo</span>
                <input-percent class="form-control" v-model="dimensaoRegra.perc_max_vinculado" />
                <span>do valor:</span>
              </div>
              <CRow>
                <CCol md="6" class="mb-2">
                  <label class="regra-modal__rotulo">Tipo de segmento</label>
                  <SeletorBusca
                    v-model="dimensaoRegra.coluna_vinculada"
                    :opcoes="opcoesColunas"
                    :mostrar-codigo="false"
                    :clearable="false"
                    placeholder="Selecione o tipo de segmento"
                    aria-label="Tipo de segmento vinculado"
                    :erro="!!errosCarga.dimensoes"
                    @abrir="$emit('recarregar', 'dimensoes')"
                    @change="changeColunaVinculadaSelecionada"
                  />
                </CCol>
                <CCol md="6" class="mb-2">
                  <label class="regra-modal__rotulo">Segmento</label>
                  <SeletorBusca
                    v-model="dimensaoRegra.dimensao_vinculada_id"
                    :opcoes="opcoesSegmentos(dimensaoRegra.coluna_vinculada)"
                    :clearable="false"
                    placeholder="Busque o segmento"
                    aria-label="Segmento vinculado"
                    :erro="!!errosCarga.dimensoes"
                    @abrir="$emit('recarregar', 'dimensoes')"
                  />
                </CCol>
              </CRow>
            </div>
          </div>
        </template>
        <div v-if="!item.dimensoes.length" class="regra-negocio-regra__vazio">Nenhuma regra de validação cadastrada.</div>
      </div>
      <small v-if="erros.dimensoes" class="regra-modal__campo-erro">{{ erros.dimensoes }}</small>

      <CButton color="primary" variant="outline" class="mt-3" @click="adicionarRegra">
        <fa-icon icon="plus" class="mr-1" />
        Adicionar regra de validação
      </CButton>
    </div>

    <!-- DESABILITADO POR MOTIVOS DE SEGURANÇA -->
    <!-- REGRA POR SCRIPT PHP -->
    <!-- <div class="xrow" v-if="item.tipo === 'SP'">
      <CodeEditor
        :key="'php-editor'"
        v-model="currentScript"
        :scriptMode="'PHP'"
      />
    </div> -->

    <QuerySql v-if="item.tipo === 'QS'" :item="item" :erros="erros" />

    <div v-if="item.tipo === 'TF'" class="regra-modal__secao">
      <div class="regra-modal__secao-titulo">Taxa fixa da cobrança</div>
      <div class="regra-modal__aviso">
        <fa-icon icon="info-circle" class="regra-modal__aviso-icone" />
        <span>
          A taxa fixa da cobrança é cobrada nos primeiros pedidos de cada cliente, até o limite no período.
          Depois disso, os pedidos seguintes do cliente saem sem a taxa até o período virar.
          As restrições, os canais e a vigência da regra definem para quem ela vale. Havendo mais de uma regra,
          vale a mais restritiva.
        </span>
      </div>
      <div class="mb-3">
        <label class="regra-modal__rotulo">Cobranças</label>
        <CobrancasTaxaFixa v-model="item.taxaFixa.cobrancas" />
        <small v-if="erros['taxaFixa.cobrancas']" class="regra-modal__campo-erro">{{ erros["taxaFixa.cobrancas"] }}</small>
      </div>
      <div class="mb-3">
        <span id="regra-negocio-modo-contagem" class="regra-modal__rotulo">Validação da taxa *</span>
        <div class="regra-negocio-regra__modos" role="radiogroup" aria-labelledby="regra-negocio-modo-contagem">
          <label
            v-for="modo in opcoesModoContagem"
            :key="modo.id"
            :class="['regra-negocio-regra__modo', { 'regra-negocio-regra__modo--ativo': item.taxaFixa.modo_contagem === modo.id }]"
          >
            <input v-model="item.taxaFixa.modo_contagem" type="radio" name="regra-negocio-modo-contagem" :value="modo.id" />
            <span class="regra-negocio-regra__modo-texto">
              <strong>{{ modo.descricao }}</strong>
              <small>{{ modo.ajuda }}</small>
            </span>
          </label>
        </div>
        <small v-if="erros['taxaFixa.modo_contagem']" class="regra-modal__campo-erro">{{ erros["taxaFixa.modo_contagem"] }}</small>
      </div>
      <div class="mb-3">
        <span id="regra-negocio-momento-contagem" class="regra-modal__rotulo">Contar o pedido *</span>
        <div class="regra-negocio-regra__modos" role="radiogroup" aria-labelledby="regra-negocio-momento-contagem">
          <label
            v-for="momento in opcoesMomentoContagem"
            :key="momento.id"
            :class="['regra-negocio-regra__modo', { 'regra-negocio-regra__modo--ativo': item.taxaFixa.momento_contagem === momento.id }]"
          >
            <input v-model="item.taxaFixa.momento_contagem" type="radio" name="regra-negocio-momento-contagem" :value="momento.id" />
            <span class="regra-negocio-regra__modo-texto">
              <strong>{{ momento.descricao }}</strong>
              <small>{{ momento.ajuda }}</small>
            </span>
          </label>
        </div>
        <small v-if="erros['taxaFixa.momento_contagem']" class="regra-modal__campo-erro">{{ erros["taxaFixa.momento_contagem"] }}</small>
      </div>
      <CRow>
        <CCol md="3" sm="6" class="mb-3">
          <label class="regra-modal__rotulo" for="regra-negocio-limite">Limite por cliente *</label>
          <input
            id="regra-negocio-limite"
            type="number"
            min="1"
            :max="limiteMaximoOcorrencias"
            step="1"
            class="form-control"
            v-model.number="item.taxaFixa.limite_ocorrencias"
          />
          <small v-if="erros['taxaFixa.limite_ocorrencias']" class="regra-modal__campo-erro">{{ erros["taxaFixa.limite_ocorrencias"] }}</small>
        </CCol>
        <CCol md="3" sm="6" class="mb-3">
          <label class="regra-modal__rotulo">Período *</label>
          <SeletorBusca
            v-model="item.taxaFixa.periodo_ocorrencias"
            :opcoes="opcoesPeriodo"
            :mostrar-codigo="false"
            :clearable="false"
            placeholder="Selecione o período"
            aria-label="Período do limite"
          />
          <small v-if="erros['taxaFixa.periodo_ocorrencias']" class="regra-modal__campo-erro">{{ erros["taxaFixa.periodo_ocorrencias"] }}</small>
        </CCol>
      </CRow>
      <small class="regra-modal__dica">{{ resumoTaxaFixa }}</small>
    </div>
  </div>
</template>

<script>
import SeletorBusca from "@/components/form/SeletorBusca.vue";
import CobrancasTaxaFixa from "./CobrancasTaxaFixa.vue";
import InterruptorCashback from "@/views/Cashback/ModalRegraCashback/components/InterruptorCashback.vue";
import ListaProdutos from "./ListaProdutos";
import ListaPromocoes from "./ListaPromocoes";
import QuerySql from "./QuerySql.vue";
import _remove from "lodash/remove";
import _filter from "lodash/filter";
import { MODOS_CONTAGEM_TAXA_FIXA, MOMENTOS_CONTAGEM_TAXA_FIXA, PERIODOS_TAXA_FIXA, TIPOS_REGRA } from "../../../constantes";
import { LIMITE_MAXIMO_OCORRENCIAS, camposAoTrocarTipo, normalizarTaxaFixa, resumoTaxaFixa } from "../../../helpers/regraNegocio";

const AJUDA_MODO_CONTAGEM = {
  COBRANCA: "Cada cobrança tem o seu limite. Com limite 1 por dia em PIX e Boleto, o cliente paga a taxa no primeiro pedido em PIX e no primeiro em Boleto.",
  PEDIDO: "Um limite único para todas as cobranças. No mesmo exemplo, depois do primeiro pedido com taxa, os seguintes em PIX ou Boleto saem sem a taxa.",
};

const AJUDA_MOMENTO_CONTAGEM = {
  ENVIO: "O pedido conta a partir do envio ao ERP.",
  FATURAMENTO: "O pedido só conta depois de faturado no ERP, pela data do faturamento. Pedidos enviados e ainda não faturados não contam.",
};

const initialDimensao = () => ({
  dimensao_id: null,
  coluna: null,
  dimensao_vinculada_id: null,
  coluna_vinculada: null,
  perc_min_vinculado: 0,
  perc_max_vinculado: 100,
  validar_todas_regras: 1,
});

export default {
  name: "Regra",
  components: {
    ListaProdutos,
    ListaPromocoes,
    QuerySql,
    SeletorBusca,
    CobrancasTaxaFixa,
    InterruptorCashback,
  },

  props: {
    item: { type: Object, required: true },
    produtos: { type: Array, default: () => [] },
    dimensoes: { type: Array, default: () => [] },
    promocoes: { type: Array, default: () => [] },
    errosCarga: { type: Object, default: () => ({}) },
    erros: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      colunaSelecionada: null,
      colunaVinculadaSelecionada: null,
      opcoesTipo: TIPOS_REGRA.map((tipo) => ({ id: tipo.chave, descricao: tipo.label, resumo: tipo.descricao, busca: tipo.descricao, icone: tipo.icone })),
      limiteMaximoOcorrencias: LIMITE_MAXIMO_OCORRENCIAS,
      opcoesPeriodo: Object.keys(PERIODOS_TAXA_FIXA).map((chave) => ({ id: chave, descricao: PERIODOS_TAXA_FIXA[chave] })),
      opcoesModoContagem: Object.keys(MODOS_CONTAGEM_TAXA_FIXA).map((chave) => ({
        id: chave,
        descricao: MODOS_CONTAGEM_TAXA_FIXA[chave],
        ajuda: AJUDA_MODO_CONTAGEM[chave],
      })),
      opcoesMomentoContagem: Object.keys(MOMENTOS_CONTAGEM_TAXA_FIXA).map((chave) => ({
        id: chave,
        descricao: MOMENTOS_CONTAGEM_TAXA_FIXA[chave],
        ajuda: AJUDA_MOMENTO_CONTAGEM[chave],
      })),
    };
  },

  computed: {
    tipoAtual() {
      return TIPOS_REGRA.find((tipo) => tipo.chave === this.item.tipo) || null;
    },

    opcoesColunas() {
      return this.dimensoes.map((dimensao) => ({ id: dimensao.coluna, descricao: dimensao.label }));
    },

    resumoTaxaFixa() {
      return resumoTaxaFixa(this.item.taxaFixa) || "Informe o limite e o período.";
    },
  },

  created() {
    this.garantirTaxaFixa();
  },

  methods: {
    selecionarTipo(novoTipo) {
      if (novoTipo === this.item.tipo) return;

      const campos = camposAoTrocarTipo(this.item, novoTipo);

      Object.keys(campos).forEach((campo) => this.$set(this.item, campo, campos[campo]));
    },

    garantirTaxaFixa() {
      const taxaFixa = this.item.taxaFixa;

      if (!taxaFixa || !Array.isArray(taxaFixa.cobrancas) || !taxaFixa.modo_contagem || !taxaFixa.momento_contagem) {
        this.$set(this.item, "taxaFixa", normalizarTaxaFixa(taxaFixa));
      }
    },

    opcoesSegmentos(coluna) {
      return (this.getItensByColunaSelecionada(coluna) || []).map((dimensao) => ({ id: dimensao.id, descricao: dimensao.desc }));
    },

    getItensByColunaSelecionada(coluna) {
      const itensDimensao = _filter(this.dimensoes, (dimensao) => {
        return +dimensao.coluna === +coluna;
      });

      return itensDimensao[0]?.dimensoes;
    },

    changeColunaSelecionada(coluna) {
      this.colunaSelecionada = coluna;
    },

    changeColunaVinculadaSelecionada(coluna) {
      this.colunaVinculadaSelecionada = coluna;
    },

    adicionarRegra() {
      this.item.dimensoes.push(initialDimensao());
    },

    excluirRegra(index) {
      this.item.dimensoes.splice(index, 1);
    },

    addProduto(produto) {
      this.item.produtos.push(produto);
    },

    removeProduto(id) {
      const produtos = [...this.item.produtos];
      _remove(produtos, (produto) => produto.produto_id === id);
      this.item.produtos = produtos;
    },

    addPromocao(promocao) {
      this.item.promocoesExcessao.push(promocao);
    },

    removePromocao(id) {
      const promocoes = [...this.item.promocoesExcessao];
      _remove(promocoes, (promocao) => promocao.promocao_id === id);
      this.item.promocoesExcessao = promocoes;
    },
  },
};
</script>

<style scoped src="@/components/regras/regra-modal.css"></style>

<style scoped>
.regra-negocio-regra__tipo {
  max-width: 520px;
}

.regra-negocio-regra__dimensoes {
  margin-top: 12px;
}

.regra-negocio-regra__dimensao {
  padding: 12px 14px 4px;
}

.regra-negocio-regra__dimensao-frase {
  margin-bottom: 8px;
  font-size: 13px;
  color: #344054;
}

.regra-negocio-regra__conector {
  display: flex;
  justify-content: center;
  margin: 8px 0;
}

.regra-negocio-regra__conector-texto {
  padding: 0 10px;
  border-radius: 9px;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0.5px;
  color: #fff;
  background-color: var(--cashback-cor-primaria);
}

.regra-negocio-regra__conector-texto--ou {
  background-color: #2298a3;
}

.regra-negocio-regra__modos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
}

.regra-negocio-regra__modo {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  cursor: pointer;
  background-color: #fff;
}

.regra-negocio-regra__modo input {
  margin-top: 3px;
  accent-color: var(--cashback-cor-primaria);
}

.regra-negocio-regra__modo--ativo {
  border-color: var(--cashback-cor-primaria);
  box-shadow: 0 0 0 1px var(--cashback-cor-primaria);
}

.regra-negocio-regra__modo-texto {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.regra-negocio-regra__modo-texto strong {
  font-size: 13px;
  color: #344054;
}

.regra-negocio-regra__modo-texto small {
  font-size: 12px;
  line-height: 1.4;
  color: #667085;
}

.regra-negocio-regra__vazio {
  padding: 24px 14px;
  border: 1px dashed #d0d5dd;
  border-radius: 10px;
  text-align: center;
  font-size: 12.5px;
  color: #98a2b3;
}
</style>
